import { IconCamera, IconCheck, IconPhotoPlus, IconX } from '@tabler/icons-react-native';
import { OpenSettingsPrompt } from 'app/components/open-settings-prompt';
import { Column, Row } from 'app/design/primitives/box';
import { Button } from 'app/design/primitives/button';
import { Gradient } from 'app/design/primitives/gradient';
import { PCImage } from 'app/design/primitives/image';
import { Text } from 'app/design/primitives/text';
import { useImagePickerCamera } from 'app/hooks/use-imagepicker-camera';
import { useTheme } from 'app/shopify';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import { CameraView, useCameraPermissions, type CameraCapturedPicture } from 'expo-camera';
import type { ImagePickerAsset } from 'expo-image-picker';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActivityIndicator, AppState, FlatList, Linking, Platform, Pressable, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

const TOP_GRADIENT_HEIGHT = 180;

function toImagePickerAsset(picture: CameraCapturedPicture): ImagePickerAsset {
  const extension = picture.format === 'png' ? 'png' : 'jpg';
  return {
    uri: picture.uri,
    width: picture.width,
    height: picture.height,
    type: 'image',
    fileName: `bulk-photo-${String(Date.now())}.${extension}`,
    mimeType: picture.format === 'png' ? 'image/png' : 'image/jpeg',
  };
}

function getErrorMessage(error: unknown) {
  return error instanceof Error ? error.message : String(error);
}

function ThumbnailSeparator() {
  return <View style={styles.thumbnailSeparator} />;
}

export function BulkCameraCapture({
  active,
  embedded = false,
  photos,
  maxPhotos,
  disabled,
  error,
  onPhotosChange,
  onCancel,
  onDone,
}: {
  active: boolean;
  embedded?: boolean;
  photos: ImagePickerAsset[];
  maxPhotos: number;
  disabled: boolean;
  error?: string;
  onPhotosChange: (photos: ImagePickerAsset[]) => void;
  onCancel: () => void;
  onDone: (photos: ImagePickerAsset[]) => void;
}) {
  const { t } = useTranslation('new');
  const theme = useTheme();
  const insets = useSafeAreaInsets();
  const [bottomBarHeight, setBottomBarHeight] = useState<number>();
  const {
    camera,
    permission,
    requestPermission,
    setReady,
    capturing,
    cameraError,
    setCameraError,
    remaining,
    isPickingImage,
    onUseImagePickerCamera,
    settingsPromptState,
    busy,
    canCapture,
    canFinish,
    takePicture,
  } = useBulkCamera({ active, photos, maxPhotos, disabled, onPhotosChange });
  const white = theme.colors['text.inverse.primary'];
  const ink = theme.colors['text.primary'];

  return (
    <View style={[styles.screen, { backgroundColor: ink }]}>
      {permission?.granted && active && !isPickingImage ? (
        <CameraView
          ref={camera}
          active
          animateShutter
          facing="back"
          // Expo uses a normalized zoom: zero is the selected rear camera's native 1× view.
          zoom={0}
          mode="picture"
          onCameraReady={() => setReady(true)}
          onMountError={(event) => {
            setCameraError(event.message);
            setReady(false);
          }}
          responsiveOrientationWhenOrientationLocked
          style={StyleSheet.absoluteFill}
        />
      ) : null}
      <Gradient from={ink} to={ink} toOpacity={0} height={TOP_GRADIENT_HEIGHT} style={styles.topGradient} />
      <View style={[styles.topBar, { paddingTop: (embedded ? 0 : insets.top) + theme.spacing['spacing.general.x4'] }]}>
        {embedded ? (
          <View style={styles.iconButtonPlaceholder} />
        ) : (
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={t('bulk.camera.close')}
            disabled={busy}
            onPress={onCancel}
            style={styles.iconButton}
          >
            <IconX color={white} size={24} strokeWidth={ICON_STROKE_WIDTH} />
          </Pressable>
        )}
        <View style={styles.counter}>
          <Text color="text.inverse.primary" fontWeight="600" variant="bodySmall" accessibilityLiveRegion="polite">
            {t('bulk.camera.counter', { count: photos.length, max: maxPhotos })}
          </Text>
        </View>
        <View style={styles.iconButtonPlaceholder} />
      </View>
      <BulkCameraViewfinder
        permission={permission}
        isPickingImage={isPickingImage}
        requestPermission={requestPermission}
        cameraError={cameraError}
        error={error}
      />
      {bottomBarHeight !== undefined && bottomBarHeight > 0 ? (
        // Anchor to the screen edge; recreate native SVG geometry when the footer grows or shrinks.
        <Gradient
          key={bottomBarHeight}
          from={ink}
          to={ink}
          fromOpacity={0}
          toOpacity={0.95}
          height={bottomBarHeight}
          style={[styles.bottomGradient, { height: bottomBarHeight }]}
        />
      ) : null}
      <View
        onLayout={({ nativeEvent }) => setBottomBarHeight(nativeEvent.layout.height)}
        style={[styles.bottomBar, { paddingBottom: Math.max(insets.bottom, theme.spacing['spacing.general.x4']) }]}
      >
        <Column gap="spacing.general.x1" alignItems="center">
          <Text color="text.inverse.primary" fontWeight="700" textAlign="center" variant="bodyBase">
            {t('bulk.camera.hint')}
          </Text>
          <Text color="text.inverse.primary" textAlign="center" variant="bodySmall">
            {remaining > 0 ? t('bulk.camera.draftHint') : t('bulk.camera.limitReached', { max: maxPhotos })}
          </Text>
        </Column>
        {photos.length > 0 ? (
          <FlatList
            testID="bulk-camera-thumbnails"
            data={photos}
            extraData={busy}
            horizontal
            showsHorizontalScrollIndicator={false}
            style={styles.thumbnailList}
            contentContainerStyle={styles.thumbnails}
            initialNumToRender={6}
            maxToRenderPerBatch={6}
            windowSize={3}
            removeClippedSubviews={false}
            getItemLayout={(_, index) => ({ length: 64, offset: index * 72, index })}
            keyExtractor={(photo) => photo.uri}
            ItemSeparatorComponent={ThumbnailSeparator}
            renderItem={({ item: photo, index }) => (
              <View style={styles.thumbnail}>
                <PCImage
                  source={{ uri: photo.uri }}
                  contentFit="cover"
                  style={[styles.thumbnailImage, { borderColor: white, borderRadius: theme.borderRadii['radius.md'] }]}
                />
                <Pressable
                  accessibilityRole="button"
                  accessibilityLabel={t('bulk.camera.removePhotoNumber', { number: index + 1 })}
                  disabled={busy}
                  onPress={() => onPhotosChange(photos.filter((_, photoIndex) => index !== photoIndex))}
                  style={styles.remove}
                >
                  <View style={styles.removeIcon}>
                    <IconX size={14} color={white} />
                  </View>
                </Pressable>
              </View>
            )}
          />
        ) : null}
        <BulkCameraControls
          busy={busy}
          remaining={remaining}
          onUseImagePickerCamera={onUseImagePickerCamera}
          canCapture={canCapture}
          capturing={capturing}
          takePicture={takePicture}
          canFinish={canFinish}
          onDone={() => onDone(photos)}
        />
      </View>
      <OpenSettingsPrompt
        isOpen={settingsPromptState.isOpen}
        onClose={settingsPromptState.onClose}
        settingName="camera"
      />
    </View>
  );
}

function BulkCameraControls({
  busy,
  remaining,
  onUseImagePickerCamera,
  canCapture,
  capturing,
  takePicture,
  canFinish,
  onDone,
}: Pick<
  ReturnType<typeof useBulkCamera>,
  'busy' | 'remaining' | 'onUseImagePickerCamera' | 'canCapture' | 'capturing' | 'takePicture' | 'canFinish'
> & { onDone: () => void }) {
  const { t } = useTranslation('new');
  const theme = useTheme();
  const white = theme.colors['text.inverse.primary'];
  return (
    <Row alignItems="center" justifyContent="space-between" gap="spacing.general.x3">
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={t('bulk.camera.library')}
        accessibilityState={{ disabled: busy || remaining === 0 }}
        disabled={busy || remaining === 0}
        onPress={() => onUseImagePickerCamera({ library: true })}
        style={[styles.sideControl, (busy || remaining === 0) && styles.disabled]}
      >
        <View style={[styles.libraryIcon, { borderColor: white, borderRadius: theme.borderRadii['radius.lg'] }]}>
          <IconPhotoPlus size={26} color={white} strokeWidth={ICON_STROKE_WIDTH} />
        </View>
        <Text color="text.inverse.primary" variant="bodySmall">
          {t('bulk.camera.library')}
        </Text>
      </Pressable>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={t('bulk.camera.capture')}
        accessibilityState={{ disabled: !canCapture, busy: capturing }}
        disabled={!canCapture}
        onPress={() => void takePicture()}
        style={[styles.shutter, { borderColor: white }, !canCapture && styles.disabled]}
      >
        {capturing ? (
          <ActivityIndicator color={white} />
        ) : (
          <View style={[styles.shutterInner, { backgroundColor: white }]} />
        )}
      </Pressable>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={t('bulk.camera.done')}
        accessibilityHint={t('bulk.camera.finishHint')}
        accessibilityState={{ disabled: !canFinish }}
        disabled={!canFinish}
        onPress={onDone}
        style={[styles.sideControl, !canFinish && styles.disabled]}
      >
        <View style={[styles.finishIcon, { backgroundColor: theme.colors['surface.brand.default'] }]}>
          <IconCheck size={26} color={white} strokeWidth={ICON_STROKE_WIDTH} />
        </View>
        <Text color="text.inverse.primary" fontWeight="700" variant="bodySmall">
          {t('bulk.camera.done')}
        </Text>
      </Pressable>
    </Row>
  );
}

function useBulkCamera({
  active,
  photos,
  maxPhotos,
  disabled,
  onPhotosChange,
}: Pick<Parameters<typeof BulkCameraCapture>[0], 'active' | 'photos' | 'maxPhotos' | 'disabled' | 'onPhotosChange'>) {
  const camera = useRef<CameraView>(null);
  const requestedPermission = useRef(false);
  const captureInFlight = useRef(false);
  const [permission, requestPermission, refreshPermission] = useCameraPermissions();
  const [ready, setReady] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [cameraError, setCameraError] = useState<string>();
  const remaining = Math.max(0, maxPhotos - photos.length);
  const { isPickingImage, onUseImagePickerCamera, settingsPromptState } = useImagePickerCamera(
    (result) => onPhotosChange([...photos, ...result.assets].slice(0, maxPhotos)),
    undefined,
    { selectionLimit: remaining, mediaTypes: ['images'] },
  );
  const busy = disabled || capturing || isPickingImage || !active;
  const canCapture = ready && !busy && remaining > 0;
  const canFinish = photos.length > 0 && !busy;

  useEffect(() => {
    if (!active || !permission || requestedPermission.current) return;
    requestedPermission.current = true;
    if (!permission.granted && permission.canAskAgain) void requestPermission();
  }, [active, permission, requestPermission]);

  if ((!active || isPickingImage) && ready) setReady(false);

  useEffect(() => {
    const listener = AppState.addEventListener('change', (state) => {
      if (state === 'active' && active) void refreshPermission();
    });
    return () => listener.remove();
  }, [active, refreshPermission]);

  const takePicture = async () => {
    if (!canCapture || captureInFlight.current) return;
    const cameraView = camera.current;
    if (!cameraView) return;
    captureInFlight.current = true;
    setCapturing(true);
    setCameraError(undefined);
    try {
      const picture = await cameraView.takePictureAsync({ quality: 0.82, exif: false, shutterSound: true });
      if (picture) {
        onPhotosChange([...photos, toImagePickerAsset(picture)].slice(0, maxPhotos));
      }
    } catch (cause) {
      setCameraError(getErrorMessage(cause));
    }
    captureInFlight.current = false;
    setCapturing(false);
  };

  return {
    camera,
    permission,
    requestPermission,
    setReady,
    capturing,
    cameraError,
    setCameraError,
    remaining,
    isPickingImage,
    onUseImagePickerCamera,
    settingsPromptState,
    busy,
    canCapture,
    canFinish,
    takePicture,
  };
}

function BulkCameraViewfinder({
  permission,
  isPickingImage,
  requestPermission,
  cameraError,
  error,
}: Pick<ReturnType<typeof useBulkCamera>, 'permission' | 'isPickingImage' | 'requestPermission' | 'cameraError'> & {
  error?: string;
}) {
  const { t } = useTranslation('new');
  const white = useTheme().colors['text.inverse.primary'];
  return (
    <View style={styles.viewfinder}>
      {!permission || isPickingImage ? (
        <ActivityIndicator color={white} size="large" />
      ) : !permission.granted ? (
        <Column gap="spacing.general.x3" alignItems="center">
          <IconCamera size={36} color={white} strokeWidth={ICON_STROKE_WIDTH} />
          <Text color="text.inverse.primary" fontWeight="700" textAlign="center" variant="bodyLarge">
            {t('bulk.camera.permissionTitle')}
          </Text>
          <Text color="text.inverse.primary" textAlign="center" variant="bodySmall">
            {t('bulk.camera.permissionDescription')}
          </Text>
          {permission.canAskAgain ? (
            <Button onPress={() => void requestPermission()} variant="primary">
              {t('bulk.camera.permissionAction')}
            </Button>
          ) : Platform.OS !== 'web' ? (
            <Button onPress={() => void Linking.openSettings()} variant="primary">
              {t('bulk.camera.settings')}
            </Button>
          ) : null}
        </Column>
      ) : null}
      {cameraError || error ? (
        <View style={styles.error} accessibilityLiveRegion="polite">
          <Text color="text.inverse.primary" textAlign="center" variant="bodySmall">
            {error ?? cameraError}
          </Text>
        </View>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  screen: { flex: 1 },
  topGradient: { position: 'absolute', top: 0, left: 0, right: 0, height: TOP_GRADIENT_HEIGHT },
  topBar: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 16 },
  iconButton: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: 'rgba(0,0,0,0.45)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  iconButtonPlaceholder: { width: 44 },
  counter: { borderRadius: 100, backgroundColor: 'rgba(0,0,0,0.45)', paddingHorizontal: 16, paddingVertical: 8 },
  viewfinder: { flex: 1, justifyContent: 'center', padding: 24 },
  error: { backgroundColor: 'rgba(0,0,0,0.7)', padding: 12, borderRadius: 12, marginTop: 12 },
  bottomGradient: { position: 'absolute', bottom: 0, left: 0, right: 0 },
  bottomBar: { gap: 16, paddingTop: 24, paddingHorizontal: 16 },
  thumbnailList: { height: 80, flexGrow: 0 },
  thumbnails: { paddingTop: 8, paddingRight: 8 },
  thumbnailSeparator: { width: 8 },
  thumbnail: { width: 64, height: 72 },
  thumbnailImage: { width: 60, height: 64, borderWidth: 2 },
  remove: {
    position: 'absolute',
    right: -4,
    top: -8,
    width: 44,
    height: 44,
    alignItems: 'flex-end',
    padding: 6,
    backgroundColor: 'transparent',
  },
  removeIcon: {
    backgroundColor: 'rgba(0,0,0,0.75)',
    width: 22,
    height: 22,
    borderRadius: 11,
    alignItems: 'center',
    justifyContent: 'center',
  },
  sideControl: { alignItems: 'center', gap: 6, flex: 1 },
  libraryIcon: {
    width: 48,
    height: 48,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0,0,0,0.45)',
  },
  finishIcon: { width: 48, height: 48, borderRadius: 24, alignItems: 'center', justifyContent: 'center' },
  shutter: { width: 80, height: 80, borderRadius: 40, borderWidth: 3, alignItems: 'center', justifyContent: 'center' },
  shutterInner: { width: 64, height: 64, borderRadius: 32 },
  disabled: { opacity: 0.4 },
});
